import { useEffect, useState } from "react";
import { Link2, Plus, Trash2, Save, Loader2, Copy } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const inputCls = "h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-xs focus:outline-none focus:ring-2 focus:ring-slate-200";
const TYPE_OPTIONS = [{ id: "visio", label: "Visio" }, { id: "telephonique", label: "Téléphonique" }, { id: "exterieur", label: "Présentiel" }];
const DURATIONS = [15, 30, 45, 60, 90, 120];

export default function RdvSettings() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/booking/config").then((r) => setCfg(r.data)).catch(() => toast.error("Chargement impossible"));
  }, []);

  const set = (k) => (e) => setCfg((c) => ({ ...c, [k]: e?.target ? e.target.value : e }));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/booking/config", {
        booking_enabled: cfg.booking_enabled, booking_title: cfg.booking_title,
        booking_description: cfg.booking_description, booking_slug: cfg.booking_slug,
        booking_brand_color: cfg.booking_brand_color, booking_reply_email: cfg.booking_reply_email,
        booking_types: cfg.booking_types, booking_durations: cfg.booking_durations,
        booking_questions: cfg.booking_questions,
        booking_email_subject: cfg.booking_email_subject, booking_email_message: cfg.booking_email_message,
        booking_confirm_email_subject: cfg.booking_confirm_email_subject,
        booking_confirm_email_message: cfg.booking_confirm_email_message,
      });
      toast.success("Paramètres du lien de réservation enregistrés");
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(cfg.link); toast.success("Lien de rendez-vous copié."); }
    catch { toast.error("Copie impossible"); }
  };

  if (!cfg) return <div className="py-10 text-center text-sm text-slate-400">Chargement…</div>;

  const toggleType = (t) => setCfg((c) => ({
    ...c, booking_types: c.booking_types.includes(t)
      ? (c.booking_types.length > 1 ? c.booking_types.filter((x) => x !== t) : c.booking_types)
      : [...c.booking_types, t],
  }));
  const toggleDuration = (d) => setCfg((c) => ({
    ...c, booking_durations: (c.booking_durations || []).includes(d)
      ? c.booking_durations.filter((x) => x !== d) : [...(c.booking_durations || []), d],
  }));
  const setQuestion = (i, patch) => setCfg((c) => ({
    ...c, booking_questions: c.booking_questions.map((q, k) => (k === i ? { ...q, ...patch } : q)),
  }));

  return (
    <div className="max-w-3xl space-y-5" data-testid="rdv-settings-panel">
      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
        <h3 className="text-sm font-semibold text-slate-900 mb-4 flex items-center gap-2"><Link2 className="w-4 h-4 text-slate-400" />Lien de réservation public</h3>
        <div className="flex items-center gap-2">
          <input readOnly value={cfg.link || ""} className={`${inputCls} flex-1 text-slate-500 bg-slate-50`} data-testid="rdv-settings-link" />
          <Button variant="outline" className="rounded-xl gap-1.5 text-xs shrink-0" onClick={copyLink} data-testid="rdv-settings-copy">
            <Copy className="w-3.5 h-3.5" />Copier
          </Button>
        </div>
        <label className="flex items-center gap-2 mt-4 cursor-pointer">
          <input type="checkbox" checked={!!cfg.booking_enabled}
            onChange={(e) => setCfg((c) => ({ ...c, booking_enabled: e.target.checked }))}
            className="rounded border-slate-300" data-testid="rdv-settings-enabled" />
          <span className="text-sm text-slate-700">Réservations en ligne activées</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Nom affiché</label>
            <input value={cfg.booking_title || ""} onChange={set("booking_title")} placeholder={cfg.app_name}
              className={`${inputCls} mt-1`} data-testid="rdv-settings-title" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Alias d'URL (facultatif)</label>
            <input value={cfg.booking_slug || ""} onChange={set("booking_slug")} placeholder="dga"
              className={`${inputCls} mt-1`} data-testid="rdv-settings-slug" />
          </div>
        </div>
        <div className="mt-4">
          <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Description affichée sur la page</label>
          <textarea value={cfg.booking_description || ""} onChange={set("booking_description")} rows={2}
            placeholder="Réservez un échange avec notre agence…"
            className="mt-1 w-full text-xs text-slate-700 bg-white border border-slate-200 rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-slate-200 resize-y"
            data-testid="rdv-settings-description" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Couleur de la page</label>
            <input type="color" value={cfg.booking_brand_color || "#0f172a"} onChange={set("booking_brand_color")}
              className="mt-1 h-9 w-16 rounded-lg border border-slate-200 cursor-pointer" data-testid="rdv-settings-color" />
          </div>
          <div>
            <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Email de réponse (reply-to)</label>
            <input value={cfg.booking_reply_email || ""} onChange={set("booking_reply_email")} placeholder={cfg.account_email}
              className={`${inputCls} mt-1`} data-testid="rdv-settings-reply" />
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid="rdv-settings-types">
        <h3 className="text-sm font-semibold text-slate-900 mb-1">Types de rendez-vous proposés</h3>
        <p className="text-xs text-slate-400 mb-3">Le visiteur choisit parmi ces types si plusieurs sont cochés.</p>
        <div className="flex flex-wrap gap-2">
          {TYPE_OPTIONS.map((t) => (
            <label key={t.id} className={`flex items-center gap-1.5 text-xs border rounded-lg px-2.5 py-1.5 cursor-pointer transition-colors ${
              cfg.booking_types.includes(t.id) ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
              data-testid={`rdv-settings-type-${t.id}`}>
              <input type="checkbox" className="hidden" checked={cfg.booking_types.includes(t.id)} onChange={() => toggleType(t.id)} />
              {t.label}
            </label>
          ))}
        </div>
        <h3 className="text-sm font-semibold text-slate-900 mt-5 mb-3">Durées proposées</h3>
        <div className="flex flex-wrap gap-2">
          {DURATIONS.map((d) => (
            <label key={d} className={`flex items-center gap-1.5 text-xs border rounded-lg px-2.5 py-1.5 cursor-pointer transition-colors ${
              (cfg.booking_durations || []).includes(d) ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}
              data-testid={`rdv-settings-duration-${d}`}>
              <input type="checkbox" className="hidden" checked={(cfg.booking_durations || []).includes(d)} onChange={() => toggleDuration(d)} />
              {d >= 60 ? `${d / 60} h` : `${d} min`}
            </label>
          ))}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid="rdv-settings-questions">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold text-slate-900">Questions avant réservation</h3>
          {(cfg.booking_questions || []).length < 8 && (
            <button onClick={() => setCfg((c) => ({ ...c, booking_questions: [...(c.booking_questions || []), { key: `question_${(c.booking_questions || []).length + 1}`, label: "", required: false }] }))}
              className="text-xs font-semibold text-[#0369A1] hover:underline flex items-center gap-1" data-testid="rdv-settings-add-question">
              <Plus className="w-3.5 h-3.5" />Ajouter
            </button>
          )}
        </div>
        <p className="text-xs text-slate-400 mb-3">Nom, email, téléphone, entreprise et objet sont déjà demandés. Ajoutez ici vos questions personnalisées.</p>
        {(cfg.booking_questions || []).length === 0 && (
          <p className="text-sm text-slate-400 py-2" data-testid="rdv-settings-no-question">Aucune question personnalisée.</p>
        )}
        <div className="space-y-2">
          {(cfg.booking_questions || []).map((q, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`rdv-settings-question-${i}`}>
              <input value={q.label} onChange={(e) => setQuestion(i, { label: e.target.value })}
                placeholder="Ex : Comment avez-vous connu l'agence ?"
                className={`${inputCls} flex-1`} data-testid={`rdv-settings-question-label-${i}`} />
              <label className="flex items-center gap-1.5 text-xs text-slate-500 whitespace-nowrap cursor-pointer">
                <input type="checkbox" checked={q.required} onChange={(e) => setQuestion(i, { required: e.target.checked })}
                  className="rounded border-slate-300" data-testid={`rdv-settings-question-required-${i}`} />
                Obligatoire
              </label>
              <button onClick={() => setCfg((c) => ({ ...c, booking_questions: c.booking_questions.filter((_, k) => k !== i) }))}
                className="p-1.5 rounded-lg hover:bg-rose-50 text-slate-300 hover:text-rose-500 transition-colors"
                data-testid={`rdv-settings-question-del-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-[0_1px_3px_rgba(0,0,0,0.04)]" data-testid="rdv-settings-emails">
        <h3 className="text-sm font-semibold text-slate-900 mb-3">Emails de confirmation</h3>
        <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Objet</label>
        <input value={cfg.booking_confirm_email_subject || ""} onChange={set("booking_confirm_email_subject")}
          className={`${inputCls} mt-1 mb-3`} data-testid="rdv-settings-confirm-subject" />
        <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Message — variables : {"{nom} {entreprise} {date} {heure} {lien_gestion}"}</label>
        <textarea value={cfg.booking_confirm_email_message || ""} onChange={set("booking_confirm_email_message")} rows={4}
          className="mt-1 w-full text-xs text-slate-700 bg-white border border-slate-200 rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-slate-200 resize-y"
          data-testid="rdv-settings-confirm-message" />
        <div className="flex justify-end mt-4">
          <Button className="bg-[#0B132B] hover:bg-[#1C2A4F] text-white rounded-xl gap-1.5 text-xs" onClick={save} disabled={saving} data-testid="rdv-settings-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
}
